import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ChevronLeft, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { DocumentAttachments } from '@/app/components/DocumentAttachments';
import { CategoryInfoForm } from './components/CategoryInfoForm';
import { getCategory, createCategory, updateCategory, listCategories } from '@/app/services/categoryService';
import { Category, CategoryCreate, CategoryUpdate } from '@/app/types/category';
import { useAuthStore } from '@/app/store/authStore';
import { AuthState } from '@/app/store/authStore';
import { User } from '@/app/types/auth';

// Define selectors for auth store
const selectUser = (state: AuthState): User | null => state.user;

export default function CategoryDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('info');
  
  // Auth state
  const user = useAuthStore(selectUser);
  const userOrgId = user?.organization_id || user?.organization?.id;

  const isEditMode = Boolean(id) && id !== 'new';
  const categoryId = id && id !== 'new' ? parseInt(id, 10) : null;

  // Fetch category data
  const { data: category, isLoading: isLoadingCategory, error: categoryError } = useQuery({
    queryKey: ['category', categoryId],
    queryFn: () => getCategory(categoryId!),
    enabled: Boolean(categoryId),
  });

  // Fetch all categories for parent dropdown
  const { data: allCategoriesData, isLoading: isLoadingAllCategories } = useQuery({
    queryKey: ['allCategories', userOrgId],
    queryFn: () => {
      if (!userOrgId) return { items: [] };
      return listCategories({ skip: 0, limit: 500, sort_by: 'name', sort_dir: 'asc' }, userOrgId);
    },
    enabled: !!userOrgId,
  });

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (data: CategoryCreate) => createCategory(data, userOrgId!),
    onSuccess: (newCategory) => {
      toast.success('Category created successfully');
      queryClient.invalidateQueries({ queryKey: ['categories'] });
      queryClient.invalidateQueries({ queryKey: ['allCategories'] });
      navigate(`/settings/categories/detail/${newCategory.id}`);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Failed to create category');
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: (data: CategoryUpdate) => updateCategory(categoryId!, data),
    onSuccess: () => {
      toast.success('Category updated successfully');
      queryClient.invalidateQueries({ queryKey: ['categories'] });
      queryClient.invalidateQueries({ queryKey: ['category', categoryId] });
      queryClient.invalidateQueries({ queryKey: ['allCategories'] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Failed to update category');
    },
  });

  const handleSubmit = (data: CategoryCreate | CategoryUpdate) => {
    if (isEditMode) {
      updateMutation.mutate(data as CategoryUpdate);
    } else {
      createMutation.mutate(data as CategoryCreate);
    }
  };

  const handleBack = () => {
    navigate('/settings/categories');
  };

  const isLoading = isLoadingCategory || isLoadingAllCategories;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (categoryError) {
    return (
      <Alert variant="destructive">
        <AlertDescription>
          Failed to load category: {(categoryError as any).message}
        </AlertDescription>
      </Alert>
    );
  }

  const allCategories = allCategoriesData?.items || [];
  // Filter out current category from parent options to prevent circular reference
  const parentCategoryOptions = isEditMode 
    ? allCategories.filter(cat => cat.id !== categoryId)
    : allCategories;

  return (
    <div className="container mx-auto py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleBack}
            className="gap-2"
          >
            <ChevronLeft className="h-4 w-4" />
            Back to Categories
          </Button>
          <h1 className="text-2xl font-bold">
            {isEditMode ? `Edit Category: ${category?.name}` : 'Create New Category'}
          </h1>
        </div>
      </div>

      {/* Main Content with Tabs */}
      <Card>
        <CardContent className="p-6">
          {isEditMode ? (
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="info">Category Information</TabsTrigger>
                <TabsTrigger value="documents">Documents</TabsTrigger>
              </TabsList>

              <TabsContent value="info" className="space-y-4 mt-6">
                <CategoryInfoForm
                  category={category}
                  onSubmit={handleSubmit}
                  isSubmitting={updateMutation.isPending}
                  parentCategories={parentCategoryOptions}
                />
              </TabsContent>

              <TabsContent value="documents" className="space-y-4 mt-6">
                <DocumentAttachments
                  entityType="category"
                  entityId={categoryId!}
                  title="Category Documents"
                  maxFileSize={10}
                  maxFiles={20}
                  enableMetadata={true}
                />
              </TabsContent>
            </Tabs>
          ) : (
            <CategoryInfoForm
              onSubmit={handleSubmit}
              isSubmitting={createMutation.isPending}
              parentCategories={parentCategoryOptions}
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
}